import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import QuoteForm from "@/components/QuoteForm";
import { SITE } from "@/data/site";

export default function Contact({ quoteMode = false }) {
  return (
    <PageTransition>
      <section className="pt-40 md:pt-56 pb-16 md:pb-24 relative overflow-hidden" data-testid={quoteMode ? "quote-page" : "contact-page"}>
        <div className="absolute inset-0 halftone-bg opacity-30 halftone-fade pointer-events-none" aria-hidden />
        <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-[color:var(--text-2)]">
            — {quoteMode ? "Request a Free Quote" : "Contact"}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-display mt-6 text-[13vw] md:text-[112px] leading-[0.94] tracking-[-0.03em] max-w-[1200px]"
          >
            {quoteMode ? (<>Tell us about your <em className="italic text-[color:var(--accent)]">project.</em></>) : (<>Let's <em className="italic text-[color:var(--accent)]">talk print.</em></>)}
          </motion.h1>
          <p className="mt-8 max-w-[680px] text-[color:var(--text-2)] text-[17px] leading-[1.65]">
            Share your requirements below and we'll respond with pricing, timeline, and recommendations. Prefer to speak with someone? Call or email us directly.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-36 bg-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 rounded-3xl bg-[color:var(--bg)] border border-black/5 p-6 md:p-10">
            <QuoteForm />
          </div>

          <aside className="lg:col-span-5 space-y-8">
            <div className="rounded-3xl bg-[color:var(--bg-charcoal)] text-white p-8 md:p-10 relative overflow-hidden">
              <div className="absolute inset-0 halftone-bg opacity-[0.06]" aria-hidden />
              <div className="relative">
                <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-white/60 mb-6">Direct contact</div>
                <ul className="space-y-6">
                  <ContactRow icon={Phone} label="Phone" value={SITE.phone} href={SITE.phoneHref} testid="contact-phone" />
                  <ContactRow icon={Mail} label="Email" value={SITE.email} href={SITE.emailHref} testid="contact-email" />
                  <ContactRow icon={MapPin} label="Studio" value={<span>{SITE.addressLines[0]}<br/>{SITE.addressLines[1]}</span>} />
                  <ContactRow icon={Clock} label="Hours" value={SITE.hours} />
                </ul>
              </div>
            </div>

            <div className="rounded-3xl bg-[color:var(--bg)] border border-black/5 p-8 md:p-10">
              <div className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-[color:var(--text-2)] mb-3">Response time</div>
              <p className="text-[15.5px] leading-[1.7] text-[color:var(--text-2)]">
                Most quote requests receive a first reply within one business day. For urgent print runs, call us directly and we'll do our best to accommodate your timeline.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PageTransition>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="mt-1 w-9 h-9 rounded-full border border-white/15 grid place-items-center flex-shrink-0">
        <Icon className="w-4 h-4 text-white/80" />
      </div>
      <div className="flex-1">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50 mb-1">{label}</div>
        <div className="text-[15.5px] text-white">{value}</div>
      </div>
      {href && <ArrowUpRight className="w-4 h-4 text-white/60 flex-shrink-0 mt-2" />}
    </div>
  );
  return href ? (
    <li><a href={href} data-testid={testid} className="block hover:opacity-90 transition-opacity">{content}</a></li>
  ) : (
    <li>{content}</li>
  );
}
